import * as THREE from 'three';
import { BLACK, DSTEEL, basic, cloth, emis, std } from '../materials.ts';
import { G, M, Ribbon, V, glowSprite, limb, wisp } from '../prims.ts';
import { begin, bonesOf, done, joint, type BaseOpts, type Model } from './types.ts';

/*
 * Mamut de ceniza (docs/CRIATURAS.md): the era II siege beast the titan dreamt out of the fire that took
 * Somnhold. A war-mammoth of grey-black ash and cinder: a high domed skull over a humped back that slopes to the
 * rump, long tusks sweeping out and curling in (iron-banded, it was a beast of war), a hanging trunk, small ears
 * and four columnar legs on broad pads. Charred hair hangs in a shaggy skirt from the flanks and belly; the ash is
 * split by thin seams of ember (restrained: it is ash, not lava). On its back it still carries the wreck of a
 * burnt war-howdah: a charred platform with snapped posts, a broken railing and the boards of a little tower,
 * lashed on with chain, still smouldering. Opaque shared materials; the ember is its one self-lit material and
 * the smoke its one translucent one, plus a few glow sprites.
 */

const UP = V(0, 1, 0);

/** Hip, knee and foot of each leg (the right side, +X; the left mirrors x): front, then rear. */
const FORE = { hip: V(0.52, 1.55, 0.72), knee: V(0.53, 0.82, 0.8), foot: V(0.53, 0.12, 0.76) };
const HIND = { hip: V(0.5, 1.5, -1.0), knee: V(0.51, 0.86, -0.9), foot: V(0.51, 0.12, -0.98) };

/** A tapering tube along a curve (tusks, the tail), capped at the tip. */
const taper = (pts: THREE.Vector3[], r0: number, r1: number, seg = 8, rad = 7): THREE.BufferGeometry => {
  const curve = new THREE.CatmullRomCurve3(pts);
  const fr = curve.computeFrenetFrames(seg, false);
  const pos: number[] = [];
  const idx: number[] = [];
  for (let i = 0; i <= seg; i++) {
    const k = i / seg;
    const c = curve.getPointAt(k);
    const r = r0 + (r1 - r0) * k;
    const n = fr.normals[i] as THREE.Vector3, b = fr.binormals[i] as THREE.Vector3;
    for (let j = 0; j < rad; j++) {
      const a = (j / rad) * Math.PI * 2;
      const p = c.clone().addScaledVector(n, Math.cos(a) * r).addScaledVector(b, Math.sin(a) * r);
      pos.push(p.x, p.y, p.z);
    }
  }
  const tip = curve.getPointAt(1).addScaledVector(fr.tangents[seg] as THREE.Vector3, r1 * 1.6);
  pos.push(tip.x, tip.y, tip.z);
  const ti = (seg + 1) * rad;
  for (let i = 0; i < seg; i++) {
    for (let j = 0; j < rad; j++) {
      const a = i * rad + j, b2 = i * rad + ((j + 1) % rad), c = a + rad, d = b2 + rad;
      idx.push(a, c, b2, b2, c, d);
    }
  }
  for (let j = 0; j < rad; j++) idx.push(seg * rad + j, ti, seg * rad + ((j + 1) % rad));
  const g = new THREE.BufferGeometry();
  g.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  return g;
};

/**
 * Bones: `body` pivots at the hind hips (so it rears on them) and carries the fore legs, the head, the tail and
 * the howdah (its own pivot: it sways behind every move); the head carries the ears and the three-part trunk;
 * the hind legs hang from the model group; each leg bends at the knee.
 */
export const mammoth = (o?: BaseOpts): Model => {
  const c = begin(o, 1.5, 53);
  const { g, R, rng } = c;
  const ash = std('#3a3a40', { roughness: 0.92, flatShading: true, bumpScale: 0.06 });
  const ashD = std('#1f1e23', { roughness: 0.95, flatShading: true, bumpScale: 0.07 });
  const hair = cloth('#1a1618', { side: THREE.DoubleSide, flatShading: true, bumpScale: 0.05 });
  const tusk = std('#c9c0ad', { roughness: 0.6 });
  const wood = std('#241c18', { roughness: 0.95, flatShading: true, bumpScale: 0.08 });
  const rag = cloth('#2b1f1c', { side: THREE.DoubleSide, bumpScale: 0.04 });
  const ember = emis('#ff8a6e', '#ff4f3a', 1.2);

  const bodyJ = joint(g, V(0, 1.5, -1.0), 'body');
  const bd = bodyJ.abs;
  const headJ = joint(bd, V(0, 2.25, 1.15), 'head');
  const hd = headJ.abs;
  const earJ = [-1, 1].map((k) => joint(hd, V(k * 0.4, 2.4, 1.24), k < 0 ? 'earL' : 'earR'));
  const t1J = joint(hd, V(0, 1.86, 1.9), 'trunk1');
  const t2J = joint(t1J.abs, V(0, 1.32, 2.04), 'trunk2');
  const t3J = joint(t2J.abs, V(0, 0.82, 2.06), 'trunk3');
  const howJ = joint(bd, V(0, 2.62, -0.45), 'howdah');
  const tailJ = joint(bd, V(0, 1.95, -1.62), 'tail');
  const foreJ = [-1, 1].map((k) => joint(bd, V(k * FORE.hip.x, FORE.hip.y, FORE.hip.z), k < 0 ? 'foreL' : 'foreR'));
  const fore2J = [-1, 1].map((k, i) => joint(foreJ[i]!.abs, V(k * FORE.knee.x, FORE.knee.y, FORE.knee.z), k < 0 ? 'foreL2' : 'foreR2'));
  const hindJ = [-1, 1].map((k) => joint(g, V(k * HIND.hip.x, HIND.hip.y, HIND.hip.z), k < 0 ? 'hindL' : 'hindR'));
  const hind2J = [-1, 1].map((k, i) => joint(hindJ[i]!.abs, V(k * HIND.knee.x, HIND.knee.y, HIND.knee.z), k < 0 ? 'hindL2' : 'hindR2'));

  /** A lump: a squashed sphere. */
  const lump = (parent: THREE.Object3D, mat: THREE.Material, r: number, x: number, y: number, z: number, sx = 1, sy = 1, sz = 1, seg = 9): THREE.Mesh => {
    const m = M(new THREE.SphereGeometry(r, seg, Math.max(5, seg - 3)), mat, parent);
    m.position.set(x, y, z);
    m.scale.set(sx, sy, sz);
    return m;
  };
  /** A thin seam of ember laid over the ash: jittered points joined by a glowing tube. */
  const crack = (parent: THREE.Object3D, pts: THREE.Vector3[], r = 0.014): void => {
    const jit: THREE.Vector3[] = [];
    pts.forEach((p, i) => {
      const q = pts[i + 1];
      jit.push(p.clone().add(V(R() * 0.04, R() * 0.04, R() * 0.04)));
      if (q) jit.push(p.clone().lerp(q, 0.5).add(V(R() * 0.07, R() * 0.06, R() * 0.07)));
    });
    M(new THREE.TubeGeometry(new THREE.CatmullRomCurve3(jit, false, 'catmullrom', 0.05), jit.length, r, 3), ember, parent);
  };
  /** A hank of charred hair: a strand hanging from `top` and tapering to a ragged point, leaning out by −`out`. */
  const hank = (parent: THREE.Object3D, top: THREE.Vector3, len: number, w: number, yaw: number, out = 0.15): void => {
    const gm = new THREE.PlaneGeometry(w, len, 1, 3);
    gm.translate(0, -len / 2, 0);
    const p = gm.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < p.count; i++) {
      const d = -p.getY(i) / len;
      p.setX(i, p.getX(i) * (1 - 0.85 * d) + R() * 0.05 * d);
      p.setZ(i, 0.06 * Math.sin(d * 3 + i));
    }
    gm.computeVertexNormals();
    const m = M(gm, hair, parent);
    m.position.copy(top);
    m.rotation.set(0, yaw, 0);
    m.rotateX(out);
  };

  // Legs: columns of ash on broad round pads with blunt nails; the hair skirt hides their tops.
  for (const [k, i] of [[-1, 0], [1, 1]] as const) {
    for (const [D, up, lo] of [[FORE, foreJ[i]!.abs, fore2J[i]!.abs], [HIND, hindJ[i]!.abs, hind2J[i]!.abs]] as const) {
      const hip = V(k * D.hip.x, D.hip.y, D.hip.z), knee = V(k * D.knee.x, D.knee.y, D.knee.z), foot = V(k * D.foot.x, D.foot.y, D.foot.z);
      limb(up, hip.clone().add(V(0, 0.1, 0)), knee, 0.33, 0.25, ash, 9);
      lump(up, ash, 0.36, hip.x, hip.y - 0.05, hip.z, 1, 1.25, 1.1);
      lump(lo, ash, 0.25, knee.x, knee.y, knee.z, 1, 1, 1, 7);
      limb(lo, knee, foot, 0.24, 0.27, ash, 9);
      const pad = M(new THREE.CylinderGeometry(0.28, 0.31, 0.15, 10), ashD, lo);
      pad.position.set(foot.x, 0.075, foot.z);
      for (let n = 0; n < 3; n++) {
        const a = (n - 1) * 0.5;
        const nail = M(new THREE.BoxGeometry(0.12, 0.08, 0.06), tusk, lo);
        nail.position.set(foot.x + Math.sin(a) * 0.29, 0.06, foot.z + Math.cos(a) * 0.29);
        nail.rotation.y = a;
      }
      crack(lo, [V(k * (D.knee.x + 0.2), 0.72, D.knee.z + 0.1), V(k * (D.knee.x + 0.24), 0.5, D.knee.z + 0.02), V(k * (D.knee.x + 0.22), 0.3, D.knee.z + 0.1)], 0.012);
    }
  }

  // The body: a barrel of ash, the shoulders heaped into a hump, the back sloping down to the rump.
  lump(bd, ash, 1, 0, 1.75, -0.15, 0.86, 0.78, 1.32, 12);
  lump(bd, ash, 0.62, 0, 2.15, 0.38, 1.25, 0.95, 1.45, 11);
  lump(bd, ash, 0.62, 0, 1.72, -1.02, 1.2, 1.05, 0.95, 10);
  lump(bd, ash, 0.6, 0, 1.6, 0.82, 1.2, 1.1, 0.9, 9);
  // The neck: a heap between the hump and the skull, so the dome rises out of the shoulders.
  lump(bd, ash, 0.5, 0, 2.2, 1.02, 1.15, 1.0, 0.9, 9);
  // Ember seams down the flanks and over the hump, where the ash has split.
  for (const k of [-1, 1]) {
    crack(bd, [V(k * 0.62, 2.35, 0.55), V(k * 0.8, 2.05, 0.35), V(k * 0.88, 1.8, 0.05), V(k * 0.85, 1.6, -0.3)]);
    crack(bd, [V(k * 0.75, 2.2, -0.65), V(k * 0.86, 1.95, -0.9), V(k * 0.88, 1.7, -1.15)], 0.012);
    glowSprite(bd, '#ff4f3a', 0.45, V(k * 0.86, 1.85, -0.1), 0.25);
  }
  crack(bd, [V(0.1, 2.78, 0.6), V(-0.05, 2.7, 0.3), V(0.08, 2.58, 0.05)], 0.013);

  // The hair: a shaggy skirt round the flanks and belly, torn into hanks at the hem, and hanks over it.
  {
    const H = 0.8, top = 1.62;
    const sk = new THREE.CylinderGeometry(0.9, 1.0, H, 28, 2, true);
    const p = sk.attributes.position as THREE.BufferAttribute;
    for (let i = 0; i < p.count; i++) {
      const y = p.getY(i);
      if (y < -H / 4) p.setY(i, y + (i % 2 ? H * 0.42 : rng() * H * 0.18));
      else p.setX(i, p.getX(i) * (1 + R() * 0.06));
    }
    sk.computeVertexNormals();
    const skirt = M(sk, hair, bd);
    skirt.position.set(0, top - H / 2, -0.15);
    skirt.scale.set(1, 1, 1.5);
    // Two rows of hanks down each flank, ragged and of every length.
    for (const k of [-1, 1]) {
      for (let n = 0; n < 8; n++) {
        const z = 0.95 - n * 0.3;
        const hump = Math.max(0, 1 - Math.abs(z - 0.4) / 0.9);
        hank(bd, V(k * (0.72 + 0.08 * hump), 2.05 + 0.25 * hump - Math.max(0, -z - 0.6) * 0.25, z + R() * 0.08), 0.6 + rng() * 0.35, 0.22, k * Math.PI / 2 + R() * 0.3, -0.18);
        hank(bd, V(k * 0.88, 1.72, z - 0.15 + R() * 0.08), 0.62 + rng() * 0.3, 0.24, k * Math.PI / 2 + R() * 0.3, -0.1);
      }
    }
    // A beard of hair hanging under the throat.
    for (let n = 0; n < 4; n++) hank(bd, V((n - 1.5) * 0.2, 1.62, 1.3 - Math.abs(n - 1.5) * 0.05), 0.75 + rng() * 0.2, 0.22, (n - 1.5) * 0.4, 0.1);
  }

  // The tail: short, ending in a tuft of charred hair.
  {
    const tl = tailJ.abs;
    M(taper([V(0, 1.95, -1.62), V(0, 1.7, -1.78), V(0, 1.4, -1.82)], 0.07, 0.035, 4, 5), ashD, tl);
    const tuft = M(new THREE.ConeGeometry(0.08, 0.28, 5), hair, tl);
    tuft.position.set(0, 1.27, -1.82);
    tuft.rotation.x = Math.PI;
  }

  // Head: a high dome of skull over a heavy brow, small deep-set ember eyes, the tusks rooted under them.
  const dome = lump(hd, ash, 0.5, 0, 2.48, 1.38, 0.95, 1.35, 0.95, 11);
  dome.rotation.x = -0.4;
  lump(hd, ash, 0.44, 0, 2.08, 1.6, 0.97, 1.15, 0.85, 10);
  lump(hd, ashD, 0.24, 0, 1.9, 1.8, 1.25, 0.8, 0.9, 8);
  // A topknot of charred hair over the dome, swept back.
  for (let n = 0; n < 3; n++) hank(hd, V((n - 1) * 0.16, 3.12 - Math.abs(n - 1) * 0.06, 1.25), 0.5, 0.2, Math.PI + (n - 1) * 0.5, 1.0);
  for (const k of [-1, 1]) {
    lump(hd, BLACK, 0.06, k * 0.33, 2.22, 1.66, 1, 0.6, 0.6, 6);
    lump(hd, ember, 0.026, k * 0.34, 2.22, 1.7, 1.2, 0.7, 0.7, 5);
    glowSprite(hd, '#ff4f3a', 0.16, V(k * 0.35, 2.22, 1.72), 0.8);
    // Tusk sockets, and the tusks: out and down, forward, then curling up and in, the tips charred black.
    lump(hd, ash, 0.15, k * 0.26, 1.9, 1.78, 1, 1, 1.3, 7);
    const pts = [V(0.26, 1.9, 1.8), V(0.42, 1.48, 2.1), V(0.56, 1.24, 2.5), V(0.56, 1.32, 2.86), V(0.4, 1.64, 3.08), V(0.2, 1.94, 3.04)].map((v) => v.setX(v.x * k));
    M(taper(pts, 0.11, 0.028, 10, 7), tusk, hd);
    // An iron band near the root (it was a beast of war) and a charred tip.
    const band = M(new THREE.TorusGeometry(0.115, 0.025, 4, 9), DSTEEL, hd);
    band.position.set(k * 0.37, 1.58, 2.03);
    band.quaternion.setFromUnitVectors(V(0, 0, 1), V(k * 0.35, -0.8, 0.6).normalize());
    const tip = M(new THREE.ConeGeometry(0.034, 0.16, 6), BLACK, hd);
    tip.position.set(k * 0.24, 1.88, 3.07);
    tip.quaternion.setFromUnitVectors(UP, V(-k * 0.55, 0.85, -0.1).normalize());
    // Small ears, ragged at the edge, on their own pivots (they flare when it panics).
    const ear = lump(earJ[k < 0 ? 0 : 1]!.abs, ashD, 0.22, k * 0.45, 2.34, 1.12, 0.28, 1.0, 0.85, 7);
    ear.rotation.y = k * 0.35;
  }
  crack(hd, [V(-0.12, 2.95, 1.2), V(0.02, 2.75, 1.5), V(-0.06, 2.5, 1.75), V(0.04, 2.3, 1.9)], 0.013);

  // The trunk: three segments tapering to a tip curled forward, ringed with ember seams.
  {
    const segs: [THREE.Object3D, THREE.Vector3, THREE.Vector3, number, number][] = [
      [t1J.abs, V(0, 1.98, 1.88), V(0, 1.32, 2.04), 0.24, 0.19],
      [t2J.abs, V(0, 1.32, 2.04), V(0, 0.82, 2.06), 0.19, 0.145],
      [t3J.abs, V(0, 0.82, 2.06), V(0, 0.42, 2.0), 0.145, 0.105],
    ];
    for (const [par, a, b, r0, r1] of segs) {
      limb(par, a, b, r0, r1, ash, 8);
      lump(par, ash, r1 * 1.02, b.x, b.y, b.z, 1, 1, 1, 7);
      const ring = M(new THREE.TorusGeometry((r0 + r1) / 2 + 0.004, 0.014, 3, 8), ashD, par);
      ring.position.copy(a).lerp(b, 0.5);
      ring.quaternion.setFromUnitVectors(V(0, 0, 1), b.clone().sub(a).normalize());
    }
    limb(t3J.abs, V(0, 0.42, 2.0), V(0, 0.3, 2.14), 0.105, 0.09, ash, 7);
    lump(t3J.abs, ashD, 0.095, 0, 0.28, 2.17, 1, 0.8, 1, 6);
    crack(t1J.abs, [V(0.12, 1.85, 1.98), V(0.13, 1.6, 2.06), V(0.11, 1.4, 2.12)], 0.01);
  }

  // The wreck of the war-howdah: a scorched blanket, a charred platform knocked askew, snapped posts with ember
  // at the broken ends, a railing hanging loose and the boards of a little tower, lashed on with chain.
  const hw = howJ.abs;
  {
    // The blanket: a half-tube over the back, its axis along the spine (tipped down to the rump).
    const blanket = M(new THREE.CylinderGeometry(0.86, 0.86, 1.3, 12, 1, true, Math.PI * 0.58, Math.PI * 0.84), rag, hw);
    blanket.rotation.x = Math.PI / 2 - 0.15;
    blanket.position.set(0, 1.85, -0.45);

    const plat = G(hw, 0, 2.72, -0.45);
    plat.rotation.set(-0.15, 0.06, 0.07);
    M(new THREE.BoxGeometry(1.2, 0.1, 1.15), wood, plat);
    for (const k of [-1, 1]) {
      const beam = M(new THREE.BoxGeometry(1.36, 0.08, 0.1), wood, plat);
      beam.position.set(0, -0.06, k * 0.48);
    }
    // Posts: [x, z, height]; the stumps are snapped short, one leans.
    const POSTS: [number, number, number][] = [[-0.54, 0.5, 0.95], [0.54, 0.5, 0.36], [0.54, -0.52, 0.8], [-0.54, -0.52, 0.22]];
    for (const [x, z, h] of POSTS) {
      const post = M(new THREE.BoxGeometry(0.1, h, 0.1), wood, plat);
      post.position.set(x, h / 2, z);
      if (h > 0.9) post.rotation.z = 0.12;
      // A splintered, smouldering top.
      const sp = M(new THREE.ConeGeometry(0.06, 0.14, 4), wood, plat);
      sp.position.set(x + (h > 0.9 ? -0.06 : 0), h + 0.05, z);
      sp.rotation.set(R() * 0.6, 0, R() * 0.6);
      const em = M(new THREE.ConeGeometry(0.035, 0.06, 4), ember, plat);
      em.position.set(x + (h > 0.9 ? -0.06 : 0), h + 0.01, z);
    }
    // A railing still on the left, the right one broken and hanging down the flank.
    const rail = M(new THREE.BoxGeometry(0.06, 0.07, 1.0), wood, plat);
    rail.position.set(-0.56, 0.62, 0);
    const hang = M(new THREE.BoxGeometry(0.06, 0.07, 0.9), wood, plat);
    hang.position.set(0.62, -0.05, -0.15);
    hang.rotation.set(0.75, 0, 0.3);
    // The boards of the little tower at the back: a parapet of planks of every height, some burnt through.
    for (let n = 0; n < 6; n++) {
      const h = [0.7, 0.45, 0.82, 0.3, 0.6, 0.38][n]!;
      const board = M(new THREE.BoxGeometry(0.17, h, 0.05), wood, plat);
      board.position.set(-0.45 + n * 0.18, h / 2 + 0.04, -0.52);
      board.rotation.z = R() * 0.12;
    }
    for (let n = 0; n < 3; n++) {
      const h = [0.5, 0.25, 0.4][n]!;
      const board = M(new THREE.BoxGeometry(0.05, h, 0.17), wood, plat);
      board.position.set(-0.56, h / 2 + 0.04, -0.33 + n * 0.18);
    }
    crack(plat, [V(-0.3, 0.07, -0.4), V(-0.05, 0.07, -0.1), V(0.25, 0.07, 0.15)], 0.012);
    glowSprite(plat, '#ff4f3a', 0.7, V(0, 0.25, -0.2), 0.22);
  }
  // Chains lashing it on: down each flank and back up, iron links.
  for (const k of [-1, 1]) {
    const curve = new THREE.CatmullRomCurve3([V(k * 0.58, 2.72, -0.05), V(k * 0.88, 2.15, -0.2), V(k * 0.92, 1.9, -0.47), V(k * 0.86, 2.1, -0.75), V(k * 0.56, 2.55, -0.9)]);
    M(new THREE.TubeGeometry(curve, 14, 0.035, 4), DSTEEL, hw);
    for (const u of [0.25, 0.5, 0.75]) {
      const lk = M(new THREE.TorusGeometry(0.07, 0.022, 3, 6), DSTEEL, hw);
      lk.position.copy(curve.getPointAt(u));
      lk.quaternion.setFromUnitVectors(V(0, 0, 1), curve.getTangentAt(u));
    }
  }

  // Ash smoke off the hump and smoke still rising from the burnt howdah.
  const ribs: Ribbon[] = [];
  const wm = basic('#3a3440', 0.4);
  const SMOKE: [THREE.Object3D, number, number, number, number][] = [
    [hw, -0.6, 3.62, 0.05, 0.9], [hw, 0.5, 3.4, -0.98, 0.7], [bd, 0.0, 2.9, 0.6, 0.6],
  ];
  for (const [par, x, y, z, h] of SMOKE) ribs.push(new Ribbon(par, wisp(x, z, h, 6).map((p) => p.add(V(0, y, 0))), 0.04, wm, rng, 0.07));
  const heart = glowSprite(bd, '#ff4f3a', 1.3, V(0, 1.55, 0.95), 0.18, true);

  // Only the boil here: every move is the clips' (anim/creatures/mammoth.ts).
  const t = (n: number): void => {
    for (const r of ribs) r.step();
    heart.material.opacity = 0.14 + ((n * 3) % 7) / 60;
  };
  t(0);
  return done(c, 3.3, t, undefined, bonesOf(c, {
    body: bodyJ.p, head: headJ.p, earL: earJ[0]!.p, earR: earJ[1]!.p, trunk1: t1J.p, trunk2: t2J.p, trunk3: t3J.p,
    howdah: howJ.p, tail: tailJ.p,
    foreL: foreJ[0]!.p, foreL2: fore2J[0]!.p, foreR: foreJ[1]!.p, foreR2: fore2J[1]!.p,
    hindL: hindJ[0]!.p, hindL2: hind2J[0]!.p, hindR: hindJ[1]!.p, hindR2: hind2J[1]!.p,
  }));
};
